/**
 * "Turns that led here" (docs/ux/artboards/optimal README decision 5): the transcript turns
 * behind an ask, shown in the decision's inspector. A derived `TranscriptView` holds the turn
 * that asked (cut at the ask, so nothing after it shows) and the turns just before it; earlier
 * turns fold behind "Show N earlier turns". Pure: the inspector renders the result with the
 * transcript-ui components.
 */
import type { TimelineEntry, TranscriptView, TurnView } from '@viko/transcript-ui'

/** How many turns the inspector shows before "Show N earlier turns". */
export const LED_TURNS = 3

function firstSeq(t: TurnView): number {
  let s = t.user?.seq ?? Number.POSITIVE_INFINITY
  for (const i of t.items) if (i.seq < s) s = i.seq
  return s
}

export interface LedTurns {
  /** The view to render: only the turns that led to the ask. */
  view: TranscriptView
  /** Turns shown. */
  shown: number
  /** Loaded turns before them, folded. */
  earlier: number
  /** Turn ids of the shown turns (for the "Led to this ask" bar). */
  ids: string[]
}

/**
 * The turns up to `askSeq`: the asking turn (items after the ask removed) and up to `count - 1`
 * turns before it (`count` = Infinity shows every loaded turn). Undefined when the ask's turn is
 * not loaded yet.
 */
export function ledTurns(
  view: TranscriptView,
  askSeq: number,
  count = LED_TURNS,
): LedTurns | undefined {
  const turns: { entry: TimelineEntry & { kind: 'turn' }; at: number }[] = []
  for (const e of view.entries)
    if (e.kind === 'turn') {
      const at = firstSeq(e.turn)
      if (at <= askSeq) turns.push({ entry: e, at })
    }
  if (!turns.length) return undefined
  const keep = Math.max(1, Math.min(turns.length, count))
  const picked = turns.slice(-keep)
  const last = picked[picked.length - 1]
  if (!last) return undefined
  const cut: TimelineEntry = {
    ...last.entry,
    turn: { ...last.entry.turn, items: last.entry.turn.items.filter((i) => i.seq <= askSeq) },
  }
  const entries = [...picked.slice(0, -1).map((t) => t.entry), cut]
  return {
    view: { ...view, entries, queued: [], activity: { ...view.activity } },
    shown: entries.length,
    earlier: turns.length - keep,
    ids: entries.map((e) => e.id),
  }
}
